// components/details/Details.tsx
import classes from './Details.module.sass'
import { ComplexOrderInterface } from '../../../../../common/types/interfaces'
import { format } from 'date-fns'
import { formatPhoneNumber } from '../../../../../common/utils/functions/formatPhoneNumber'
import { IconButton } from '../../../../../common/components/iconButton/IconButton'
import print from '../../../../../assets/images/icons/print.svg'
import edit from '../../../../../assets/images/icons/edit.svg'
import { usePrint } from '../../../../../common/hooks/usePrint'
import { ComplexOrderStatus } from '../../../../../common/types/enums'

type Props = {
  el?: ComplexOrderInterface
  onEdit?: (el: ComplexOrderInterface) => void
}

const getStatusText = (status: ComplexOrderStatus): string => {
  const map: Record<number, string> = {
    [ComplexOrderStatus.DRAFT]: 'Новая',
    [ComplexOrderStatus.SENT_TO_MANAGER]: 'Отправлена',
    [ComplexOrderStatus.IN_PROGRESS]: 'В работе',
    [ComplexOrderStatus.APPROVED]: 'Согласована',
    [ComplexOrderStatus.CONVERTED]: 'Конвертирована',
    [ComplexOrderStatus.ARCHIVED]: 'Архив',
  }
  return map[status] || 'Неизвестно'
}

const getStatusColor = (status: ComplexOrderStatus): string => {
  const map: Record<number, string> = {
    [ComplexOrderStatus.DRAFT]: '#3498db',
    [ComplexOrderStatus.SENT_TO_MANAGER]: '#3498db',
    [ComplexOrderStatus.IN_PROGRESS]: '#f39c12',
    [ComplexOrderStatus.APPROVED]: '#2ecc71',
    [ComplexOrderStatus.CONVERTED]: '#2ecc71',
    [ComplexOrderStatus.ARCHIVED]: '#95a5a6',
  }
  return map[status] || '#95a5a6'
}

export const Details = ({ el, onEdit }: Props) => {
  const { reactToPrintFn, contentRef } = usePrint()

  if (el)
    return (
      <div>
        <div className={classes.container}>
          <div className={classes.content} ref={contentRef}>
            <div className={classes.title}>клиент</div>
            <div className={classes.item}>
              <div className={classes.label}>Телефон</div>
              <div className={classes.value}>{formatPhoneNumber(el.phone_number)}</div>
            </div>
            {el.customerName && (
              <div className={classes.item}>
                <div className={classes.label}>Имя</div>
                <div className={classes.value}>{el.customerName}</div>
              </div>
            )}

            <div className={classes.title}>детали заявки</div>
            <div className={classes.row}>
              <div className={classes.id}>
                <div className={classes.label}>id</div>
                <div className={classes.value}>{el.id}</div>
              </div>
              {el.createdAt && (
                <div className={classes.item}>
                  <div className={classes.label}>Создана</div>
                  <div className={classes.value}>
                    {format(new Date(el.createdAt), 'dd.MM.yyy HH:mm')}
                  </div>
                </div>
              )}
              {el.updatedAt && (
                <div className={classes.item}>
                  <div className={classes.label}>Обновлена</div>
                  <div className={classes.value}>
                    {format(new Date(el.updatedAt), 'dd.MM.yyy HH:mm')}
                  </div>
                </div>
              )}
            </div>

            <div className={classes.row}>
              <div className={classes.item}>
                <div className={classes.label}>Статус</div>
                <div className={classes.value} style={{ color: getStatusColor(el.status) }}>
                  {getStatusText(el.status)}
                </div>
              </div>
              <div className={classes.item}>
                <div className={classes.label}>Сумма</div>
                <div className={classes.value}>
                  {el.total > 0 ? `${el.total.toLocaleString('ru-RU')} ₽` : '—'}
                </div>
              </div>
            </div>

            {el.notes && (
              <div className={classes.item}>
                <div className={classes.label}>Комментарий клиента</div>
                <div className={classes.value}>{el.notes}</div>
              </div>
            )}

            {el.items && el.items.length > 0 && (
              <>
                <div className={classes.title}>состав заявки</div>
                {el.items.map((item, index) => (
                  <div key={index}>
                    <div className={classes.row}>
                      <div className={classes.name}>
                        <div className={classes.label}>Наименование</div>
                        <div className={classes.itemValue}>{item.name}</div>
                      </div>
                      <div className={classes.item}>
                        <div className={classes.label}>Количество</div>
                        <div className={classes.itemValue}>{item.quantity}</div>
                      </div>
                      <div className={classes.item}>
                        <div className={classes.label}>Цена базовая</div>
                        <div className={classes.itemValue}>
                          {item.basePrice > 0 ? `${item.basePrice.toLocaleString('ru-RU')} ₽` : '—'}
                        </div>
                      </div>
                      <div className={classes.item}>
                        <div className={classes.label}>Сумма</div>
                        <div className={classes.itemValue}>
                          {item.total > 0 ? `${item.total.toLocaleString('ru-RU')} ₽` : '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {el.data && el.data.length > 0 && (
              <>
                <div className={classes.title}>заполненные поля</div>
                {el.data.map((item, index) => (
                  <div key={index} className={classes.row}>
                    <div className={classes.item}>
                      <div className={classes.label}>Поле {item.fieldTemplateId || index + 1}</div>
                      <div className={classes.value}>{item.value}</div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
        <div className={classes.button}>
          {onEdit && (
            <IconButton small display onClick={() => onEdit(el)} icon={edit} alt="Редактировать" />
          )}
          <IconButton small display onClick={reactToPrintFn} icon={print} alt="Печать" />
        </div>
      </div>
    )
}
